// createPlatformClient — the one thing application developers consume today.
//
// Hides the OIDC -> STS -> S3 dance. After `init()`, callers ask for an S3
// client and we hand them one whose credentials are still alive. If the temp
// creds are close to expiry we re-exchange transparently; if the ID token
// itself has aged out, the caller has to re-login (we don't refresh tokens
// here yet — the spine demo only needs a single hour-long session).

// jsDelivr's `+esm` endpoint serves a Rollup-bundled copy that honors the
// package's `browser` conditional exports. esm.sh resolved the Node entry
// instead, which pulled in `fs.readFile` via unenv and blew up at runtime.
import {
  ListObjectsV2Command,
  S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";

import {
  type AnalyticsView,
  type AnalyticsViewDef,
  type AppendOpts,
  type CryptoAdapter,
  type DocumentStore,
  type DocumentsConfig,
  type ListOpts,
  type RecipientPrivateKey,
  type RecipientPublicKey,
  type Rollup,
  type RollupDef,
  createAnalytics,
  createDocuments,
  createEnvelopeAdapter,
  createRollups,
} from "@s3p/s3db";

import { type AuthModule, createAuth } from "./auth.ts";
import {
  type Collection,
  type CollectionConfig,
  type PartitionedCollection,
  createCollection,
} from "./collection.ts";
import {
  ROOT_SLUG,
  hostedUiDomain,
  platformSecretsKeyAliasArn,
  isRootSlug,
  platformBucketName,
  rootBucketName,
} from "./conventions.ts";
import { type DataProvider, type ProviderCapabilities, S3_CAPABILITIES } from "./data-provider.ts";
import { type PermissionsModule, createPermissionsModule } from "./permissions-module.ts";
import { type DocConfig, type DocHandle, createDoc } from "./doc.ts";
import { type DbAdminModule, createDbAdmin } from "./db-admin.ts";
import { type DeviceLinkModule, createDeviceLink } from "./device-link-relay.ts";
import { type DomainsModule, createDomains } from "./domains.ts";
import { type OriginsModule, createOrigins } from "./origins.ts";
import { type EmailModule, createEmail } from "./email.ts";
import { SessionExpiredError, type SessionExpiredReason, isNotAuthorizedError } from "./errors.ts";
import { type PlatformEvents, createEvents } from "./events.ts";
import { type FilesModule, createFiles } from "./files.ts";
import { type FunctionsModule, createFunctionsAdmin } from "./functions-admin.ts";
import { type IdentityModule, type IdentityStorage, createIdentity } from "./identity.ts";
import { createInvoker } from "./invoker.ts";
import { type Me, deriveMe } from "./me.ts";
import { type MetricsModule, createMetrics } from "./metrics.ts";
import {
  type JwtClaims,
  type OidcSession,
  createOidcClient,
  jwtExpired,
  jwtExpiry,
  parseJwt,
} from "./oidc.ts";
import { type OperatorsModule, createOperators } from "./operators.ts";
import { paths } from "./paths.ts";
import { type ProjectsModule, createProjects } from "./projects.ts";
import { type RolesModule as AppRolesModule, createRolesModule } from "./roles-module.ts";
import { type RolesModule, createRoles } from "./roles.ts";
import { type CallOpts, type CallsModule, createCalls } from "./calls.ts";
import { type SecretsModule, createSecrets } from "./secrets.ts";
import {
  type StsCredentials,
  getCredentialsViaIdentityPool,
  getVisitorCredentialsViaIdentityPool,
} from "./sts.ts";
import { type TelemetryModule, createTelemetry } from "./telemetry.ts";
import { type TransactionResult, type TransactionStep, createTransaction } from "./transaction.ts";
import { type UsersModule, createUsers } from "./users.ts";
import { type AuthAdminModule, createAuthAdmin } from "./auth-admin.ts";

const CREDS_REFRESH_SKEW_MS = 60_000;
// How far before the id_token's own `exp` we proactively refresh in the
// background, so a long-open tab rolls over before AWS ever sees a dead
// token. Larger than the on-demand skew to leave room for clock drift +
// the refresh round-trip.
const PROACTIVE_REFRESH_LEAD_MS = 90_000;

// AWS doesn't give us a typed error code over these hand-rolled wire calls,
// so we match the message shapes that mean "the web-identity token is no
// longer accepted": Cognito Identity Pools say NotAuthorizedException /
// "Invalid login token. Token expired"; STS says ExpiredTokenException /
// "Token is expired" / "Token expired".
//
// CAREFULLY not matched: the Identity Pool's Deny gate ("Access to Identity
// '...' is forbidden") is ALSO a NotAuthorizedException, but it means a valid
// token was refused (no role-mapped group) — refreshing and retrying can't fix
// it, and tearing the session down for it hid the real story. sts.ts raises it
// as a typed NotAuthorizedError, which must propagate to the caller untouched.
function isExpiryError(e: unknown): boolean {
  if (isNotAuthorizedError(e)) return false;
  const m = e instanceof Error ? e.message : String(e);
  if (/forbidden/i.test(m)) return false;
  return (
    /NotAuthorizedException/.test(m) || /ExpiredToken/.test(m) || /Token (?:is )?expired/i.test(m)
  );
}

export interface PlatformConfig {
  /** Project slug — the platform reserves one S3 bucket per slug, named
   *  `s3p-{accountId}-{slug}` (see conventions.ts). Apps don't pass a
   *  bucket name directly; it's derived. */
  slug: string;
  aws: {
    /** Region where the user pool + S3 bucket live (e.g. `eu-central-1`). */
    region: string;
    /** 12-digit AWS account id — with region+slug it derives the bucket,
     *  hosted-UI domain, KMS alias and every ARN. */
    accountId: string;
  };
  cognito: {
    /** Cognito User Pool ID — used as the OIDC `iss` value. */
    poolId: string;
    /** The SPA App Client — the only client; everything signs in with it. */
    clientId: string;
    /** Cognito Identity Pool id — the SDK exchanges the id_token here
     *  (the pool picks the IAM role from `cognito:groups`). */
    identityPoolId: string;
    /** Optional hosted-UI domain override; absent = the provisioning
     *  convention `{accountId}-{slug}.auth.{region}.amazoncognito.com`. */
    domain?: string;
  };
  /** OPERATOR FEDERATION (console only). When set, the client signs in
   *  against the PLATFORM pool and exchanges that token at THIS project's
   *  Identity Pool using `consoleAuth.poolId` as the provider — so a platform
   *  operator's `{slug}:operator-*` group maps to this project's owner/admin
   *  role. `cognito.*` still describes THIS project, so `app.users` keeps
   *  targeting the project pool. The platform's hosted-UI domain and bucket
   *  derive by convention (ROOT_SLUG + accountId + region). */
  consoleAuth?: {
    poolId: string;
    clientId: string;
    identityPoolId: string;
  };
  redirectUri?: string;
  postLogoutRedirectUri?: string;
  /** Where OIDC tokens are kept. Defaults to browser-wide localStorage. */
  tokenStorage?: Storage;
  /** Optional override for the identity module's key storage. Defaults
   *  to IndexedDB in browsers, in-memory elsewhere. Tests pass a Map-
   *  backed storage so they don't depend on IndexedDB availability. */
  identityStorage?: IdentityStorage;
  /** TEST SEAM — bypasses OIDC/STS. When set, the SDK uses these
   *  pre-resolved values instead of the real handshake. Production
   *  callers must NOT pass this; integration tests use it to drive
   *  the full SDK surface without standing up a live Keycloak/MinIO.
   *  Marked with a leading underscore so it stands out in reviews. */
  _testHarness?: {
    claims: JwtClaims;
    session?: OidcSession;
    s3: S3Client;
    groupResolver?: (group: string) => Promise<string[]>;
    /** Stand-in for the per-function Lambda. `calls.invoke()` invokes the real one
     *  (invoker.ts), which tests can't reach — so they inject a fake here that
     *  plays the runtime: run the handler, write the call-result object.
     *  Without it, `call()` throws rather than silently going nowhere. */
    enqueue?: (name: string, input: unknown, taskId: string) => Promise<void>;
  };
}

/** How the SDK resolves "who can read this object" for an encrypted store.
 *  Forms:
 *    - `{ group: "g1" }`         → all current members of Keycloak group g1 + the group's role pubkey
 *    - `{ role: "moderator" }`   → just the role's pubkey (anyone holding the role key reads)
 *    - `{ subs: [...] }`         → explicit pubkeys, fetched from users/{sub}/pubkey
 *    - (ctx) => RecipientSpec    → caller-resolved per-call
 *  Returning `null` from the callback opts out of encryption for that call. */
export type RecipientSpec =
  | { group: string }
  | { role: string }
  | { subs: string[] }
  | { recipients: RecipientPublicKey[] };

export type PlatformRecipients<TCtx> =
  | RecipientSpec
  | ((ctx: TCtx) => RecipientSpec | Promise<RecipientSpec>);

/** Friendly string vocabulary for `encryptedFor` — the user-facing
 *  rename of the old `recipients` field. The SDK normalizes these into
 *  the existing `RecipientSpec` shape, which the crypto adapter
 *  consumes. New code should use this; old code keeps working via the
 *  deprecated `recipients` field below.
 *
 *  - `"author"`     → wrap to the current session's own pubkey
 *  - `"role:X"`     → wrap to role X's pubkey (anyone holding the role key reads)
 *  - `"group:X"`    → wrap to current members of Cognito group X
 *  - `{ subs: [...] }` / `{ recipients: [...] }` → explicit
 *
 *  Note: `"server"` is reserved — it requires a project-server pubkey
 *  publish convention that isn't wired yet. Will throw with a clear
 *  error pointing at the unfinished work. */
export type EncryptionSpec =
  | "author"
  | "server"
  | `role:${string}`
  | `group:${string}`
  | { subs: string[] }
  | { recipients: RecipientPublicKey[] };

export type EncryptedFor<TCtx> =
  | EncryptionSpec
  | ((ctx: TCtx) => EncryptionSpec | Promise<EncryptionSpec>);

/** Config passed to platform.documents(...) — the legacy `documents`
 *  surface. New code uses `app.log(name, opts)` which translates to
 *  this shape internally. `recipients` is the deprecated name for
 *  `encryptedFor`; both are accepted, `encryptedFor` wins if both set. */
export type PlatformDocumentsConfig<TCtx> = Omit<
  DocumentsConfig<TCtx>,
  "getClient" | "bucket" | "crypto"
> & {
  bucket?: string;
  /** Opt into client-side encryption — friendly string vocabulary. */
  encryptedFor?: EncryptedFor<TCtx>;
  /** @deprecated — renamed to `encryptedFor`. Still accepted for one
   *  minor version so Rooms doesn't have to migrate in lockstep. */
  recipients?: PlatformRecipients<TCtx>;
};

/** Config for the new `app.log()` surface. Identical to documents but
 *  the name is interpreted RELATIVE to `{bucketPrefix}data/` so apps
 *  don't have to repeat the data-prefix math. */
export interface PlatformLogConfig<TCtx> {
  partition?: "author";
  encryptedFor?: EncryptedFor<TCtx>;
}

/** Where a collection/doc lives:
 *  - `"shared"` (default): `s3db/shared/{name}` — readable by EVERY signed-in
 *    user of the app (the platform's shared-by-default space).
 *  - `"private"`: `s3db/private/{sub}/{name}` — the caller's per-user space.
 *    IAM grants read/write/list on your own sub's prefix only, so other
 *    members can neither read nor enumerate it (the runtime Lambda and
 *    read-all roles like owner still can). Requires a signed-in session.
 *  - `"admin"`: `s3db/admin/{name}` — only roles holding `admin-data`.
 *
 *  Re-exported from data-provider.ts — the scope is a PROVIDER concept (a
 *  Postgres provider maps `private` to an owner_sub RLS predicate), not an
 *  S3 path detail. */
export type { DataScope } from "./data-provider.ts";
import type { DataScope } from "./data-provider.ts";

export interface PlatformCollectionConfig<T> extends CollectionConfig<T> {
  scope?: DataScope;
  /** `"author"` groups records by who wrote them: `put` lands in the
   *  signed-in user's own partition and full keys are `{sub}/{key}`.
   *  This is what makes an `"<store>/own"` grant in s3p.permissions.json
   *  REAL — the IAM policy matches the author segment, so "write own
   *  records, read everyone's" is enforced by S3, not by app code.
   *  Portable contract: "records are grouped by author" (an author
   *  column + RLS on a SQL provider). Incompatible with `indexes` and
   *  redundant with `scope: "private"` — both throw. */
  partition?: "author";
}

/** What `app.collection(name, { partition: "author" })` returns —
 *  the collection plus author-scoped views. `list`/`scan`/`subscribe`
 *  cover every author; the views scope to one. */
export interface AuthorPartitionedCollection<T> extends Collection<T> {
  /** View of one author's partition — bare keys, partition-only listing. */
  partition(sub: string): Collection<T>;
  /** The signed-in user's own partition (throws when signed out). */
  mine(): Collection<T>;
  /** Alias of `partition` that reads like the manifest ("own records"). */
  byAuthor(sub: string): Collection<T>;
}

export interface PlatformDocConfig extends DocConfig {
  scope?: DataScope;
}

export interface PlatformClient {
  init(): Promise<void>;
  login(): Promise<void>;
  logout(): void;
  isAuthenticated(): boolean;
  getClaims(): JwtClaims | null;
  getSession(): OidcSession | null;
  getCredentials(): Promise<StsCredentials>;
  getS3Client(): Promise<S3Client>;
  /** Anonymous (visitor) credentials via the Identity Pool's unauthenticated
   *  role — no sign-in. Throws if the pool disallows anonymous identities. */
  getVisitorCredentials(): Promise<StsCredentials>;
  /** S3 client scoped to the visitor (unauthenticated) role — public access. */
  getVisitorS3Client(): Promise<S3Client>;

  // ─── New v1 surface ──────────────────────────────────────────────
  /** Structured view of the current session — sub, username, roles,
   *  highest tier, groups. `null` when not signed in. Equivalent to
   *  poking at `getClaims()` but with the JWT spelunking done for you. */
  readonly me: Me | null;
  /** Convenience boolean. Same as `isAuthenticated()`, named for the
   *  `if (app.signedIn) {...}` idiom. */
  readonly signedIn: boolean;
  /** Convenience aliases for the established names. NOTE: `signIn()` is the
   *  hosted-UI REDIRECT flow — kept as the escape hatch for third-party IdP
   *  federation. The default is the native `auth` module below. */
  signIn(opts?: { identityProvider?: string }): Promise<void>;
  signOut(): void;
  /** NATIVE authentication (no hosted UI, no redirect, no callback URLs):
   *  password sign-in/up, email confirmation, forgot-password, and the
   *  challenge loop (temp-password rotation, MFA, OTP). On success the
   *  session is adopted live — no page reload required. */
  auth: AuthModule;
  /** Custom permissions + function-invoke checks, resolved from the JWT's
   *  groups × the compiled map (`.s3p/permissions.compiled.json`).
   *  ADVISORY in the browser (UI gating) — a custom permission is enforced
   *  only where a server function checks `ctx.caller.require(...)`, or by
   *  IAM for storage/function grants. */
  permissions: PermissionsModule;
  /** Shorthand for `permissions.can` — `await app.can("reports:approve")`,
   *  `await app.can("fn:generate-report")`. False when signed out. */
  can(id: string): Promise<boolean>;
  /** Open a keyed JSON collection at `s3db/{scope}/{name}/`.
   *  Replaces the raw-S3 manifest-list pattern. With
   *  `partition: "author"` the collection is grouped by writer and gains
   *  `mine()`/`byAuthor()` views — the layout that makes `"<store>/own"`
   *  permission grants enforceable (see PlatformCollectionConfig). */
  collection<T = unknown>(
    name: string,
    config: PlatformCollectionConfig<T> & { partition: "author" },
  ): AuthorPartitionedCollection<T>;
  collection<T = unknown>(name: string, config?: PlatformCollectionConfig<T>): Collection<T>;
  /** Open an append-only log STREAM at `logs/{name}/`. Not part of the
   *  database: logs live under their own top-level root. Members read every
   *  stream; author-partitioned streams (`partition: "author"`) let each
   *  member append into their own partition, flat streams need a role grant. */
  log<T = unknown, TCtx = void>(
    name: string | ((ctx: TCtx) => string),
    config?: PlatformLogConfig<TCtx>,
  ): DocumentStore<T, TCtx>;
  /** Discover WHICH log streams exist — the top-level folder names under
   *  `logs/`. `app.log(name)` reads one stream; this answers "what streams are
   *  there to read?" (the console's Logs panel nav, any log dashboard). */
  logStreams(): Promise<string[]>;
  /** Open a single named JSON blob. Supports `{token}` interpolation
   *  against `me` — e.g. `app.doc("users/{sub}/profile")`. */
  doc<T = unknown>(name: string, config?: PlatformDocConfig): DocHandle<T>;
  /** Telemetry for this project — function metrics and alarm states, read from
   *  CloudWatch in the browser. Requires the `observe` capability. */
  metrics: MetricsModule;
  /** Product analytics: `app.telemetry.track("signed_up")`. Events are JSON
   *  objects in this project's OWN bucket (`.s3p/analytics/`), written by the
   *  browser — no third-party script, no cookies, nothing leaves the account.
   *  Honors Do-Not-Track. (Distinct from `app.analytics`, the DuckDB view
   *  engine.) */
  telemetry: TelemetryModule;
  /** The active DATA PROVIDER behind `collection`/`doc` — its name and
   *  capability flags (atomicTx / analytics / liveSubscribe /
   *  rowLevelSecurity). Default: the built-in S3 provider (s3db). An app
   *  that outgrows S3 swaps this without changing call sites; branch on
   *  `app.data.capabilities` instead of assuming S3 semantics.
   *  See docs/DATA-PROVIDER.md. */
  data: DataProvider;
  /** Define an analytical view — hoist of `analytics.define()`. */
  view<TCtx>(def: AnalyticsViewDef<TCtx>): AnalyticsView<TCtx>;
  /** Bind a function name to a callable: `const provision =
   *  app.fn("provision-project"); await provision(input);`. Sugar
   *  over `functions.invoke(name, input, opts)`. */
  fn(name: string): (input?: unknown, opts?: CallOpts) => Promise<unknown>;
  /** EVERYTHING about this project's functions, one module: the registry
   *  (name → Function URL) + CloudWatch execution logs (the console's
   *  Functions panel), and the invocation ledger (`invoke`, `awaitResult`,
   *  `listInflight`, `dismissInflight` — records under `.s3p/functions/`). */
  functions: FunctionsModule & CallsModule;
  /** Read project secrets from `{bucketPrefix}secrets/`. Server-mode
   *  only — the IAM model gates the prefix to `{slug}-server`
   *  sessions. End-user sessions get 403. */
  secrets: SecretsModule;
  /** Send email from this project's domain (`noreply@{slug}.s3p.dev` by
   *  default). Enqueues the built-in `send-email` server function — the
   *  browser never holds SES rights. */
  email: EmailModule;
  /** Attach custom domains (web + email) to this project. Enqueues the
   *  privileged `provision-domain` / `verify-domain` platform functions. */
  domains: DomainsModule;
  /** Browser origins this project's bucket accepts (its CORS rule). The door
   *  for native shells — Capacitor, Electron, Tauri, extensions — whose origin
   *  is not a DNS host and so cannot be attached with `domains.add`. Direct
   *  S3, gated by the `manage-domains` capability. */
  origins: OriginsModule;
  /** Device-link sign-in relay — the approve side of `s3p connect`. Rides
   *  visitor STS; the crypto lives in `@s3p/sdk/device-link`. */
  deviceLink: DeviceLinkModule;
  /** Blob/file storage (photos, uploads) under `files/`, scoped per-user by the
   *  `${jwt:sub}` IAM floor. Distinct from s3db (collection/doc/log) — use this
   *  for binary assets, even if structured data lives in an external DB. */
  files: FilesModule;
  /** Cognito User Pool admin — caller's IAM determines what succeeds. */
  users: UsersModule;
  /** Sign-in method configuration for THIS app's pool (factors, third-party
   *  providers) — the console's Sign-in panel. IAM-gated: succeeds only for
   *  roles holding `manage-users` (admin) or owner. */
  authAdmin: AuthAdminModule;
  /** Operator management — the platform identities allowed into this project's
   *  `/server`. `null` unless the client is configured for operator federation
   *  (a federated `/server` console). */
  operators: OperatorsModule | null;
  /** Project lifecycle (create/delete) — PLATFORM business, so non-null only
   *  on the root project's client (whose runtime hosts the handlers). */
  projects: ProjectsModule | null;
  /** Role taxonomy management. `null` if the project has no Identity Pool. */
  roleAdmin: AppRolesModule | null;
  /** ADMIN database inspector — the classified tree of what's stored plus raw
   *  read/write/delete (the console's Database panel engine). IAM-gated like
   *  everything else; distinct from the typed `collection`/`doc` layer. */
  dbAdmin: DbAdminModule;
  /** Best-effort sequential write group (NOT atomic). Returns rollback
   *  fn that compensates with DELETEs on partial failure. */
  transaction: (steps: TransactionStep[]) => Promise<TransactionResult>;

  // ─── Legacy surface (still public, used by Rooms + tests) ────────
  /** @deprecated — renamed to `log()`. Stays for one minor version
   *  so Rooms doesn't have to migrate in the same PR. */
  documents<T = unknown, TCtx = void>(
    config: PlatformDocumentsConfig<TCtx>,
  ): DocumentStore<T, TCtx>;
  /** Define a rollup: documents → per-author Parquet shards.
   *  `run()` is scoped to the current author (sub from JWT). */
  rollups: {
    define<TCtx, T>(def: RollupDef<TCtx, T>): Rollup<TCtx>;
  };
  /** Define an analytical view backed by DuckDB-WASM over a set of
   *  Parquet shards. First `.query()` call dynamic-imports DuckDB
   *  (~33 MB) — subsequent calls reuse the instance. */
  analytics: {
    define<TCtx>(def: AnalyticsViewDef<TCtx>): AnalyticsView<TCtx>;
  };
  /** Subscribe to SDK events (devtools overlay, logging, telemetry). */
  events: PlatformEvents;
  /** User keypair lifecycle — generate-or-load, publish pubkey to
   *  `users/{sub}/pubkey`, fetch other users' pubkeys. The current
   *  user's sub is taken from the JWT, so callers usually don't pass
   *  it explicitly (see the no-arg convenience methods). */
  identity: {
    /** Get-or-create the CURRENT user's keypair. Bootstraps on first
     *  login; idempotent on subsequent calls. Throws if not authenticated. */
    ensureKeys(): Promise<{ publicKey: CryptoKey; privateKey: CryptoKey }>;
    /** Publish the current user's pubkey to S3. Idempotent. */
    publishPublicKey(): Promise<{ key: string; sizeBytes: number }>;
    /** Fetch another user's pubkey (or the current user's, equivalently). */
    fetchPublicKey(sub: string): Promise<CryptoKey>;
    /** Access the raw module for advanced callers (e.g. passing sub explicitly). */
    raw: IdentityModule;
  };
  /** Time-bounded role-key cache. `refresh()` re-fetches grants
   *  matching the current user's group claims; cached keys drain
   *  on TTL. */
  roles: RolesModule;
  /** Override how the SDK resolves a Keycloak group's membership
   *  into a list of subs at WRITE time. Default reads the `groups`
   *  claim from the current JWT — works for the common case of
   *  "encrypt to anyone currently in my own group." For groups the
   *  CURRENT user isn't in, callers must override this hook (the
   *  group resolver typically goes through the admin client). */
  setGroupResolver(resolver: (group: string) => Promise<string[]>): void;
}

interface ClientState {
  session: OidcSession | null;
  claims: JwtClaims | null;
  creds: StsCredentials | null;
  s3: S3Client | null;
}

export function createPlatformClient(config: PlatformConfig): PlatformClient {
  const {
    slug,
    aws,
    cognito,
    redirectUri = `${window.location.origin}/`,
    postLogoutRedirectUri = `${window.location.origin}/`,
    tokenStorage,
  } = config;

  // Per-app bucket — derived once from {accountId, slug}. No bucketPrefix
  // exists at this layer; the bucket belongs entirely to this project.
  const accountId = aws.accountId;
  const bucket = platformBucketName(accountId, slug);
  const bucketPrefix = "";

  // Sign in against a DIFFERENT pool than this project's own when configured:
  //  - consoleAuth → operator federation (regular projects; lights up app.operators)
  // The PLATFORM project has no such override: its own pool IS the operator
  // directory (console:owner/admin), so the console signs in natively.
  // (The old `adminAuth` — a separate invite-only "platform-server" POOL — was
  // retired with that pool. A stale adminAuth block left in a committed
  // s3p.project.json pointed /server at a deleted client; the field is gone so
  // it can't resurrect.)
  const authPool = config.consoleAuth;
  // Hosted-UI domains derive by convention (only the dormant redirect flow
  // uses them): the project's own, or — federated — the PLATFORM's.
  const oidc = createOidcClient({
    domain: authPool
      ? hostedUiDomain(accountId, ROOT_SLUG, aws.region)
      : (cognito.domain ?? hostedUiDomain(accountId, slug, aws.region)),
    clientId: authPool?.clientId ?? cognito.clientId,
    redirectUri,
    postLogoutRedirectUri,
    tokenStorage,
  });

  const events = createEvents();

  const state: ClientState = {
    session: null,
    claims: null,
    creds: null,
    s3: null,
  };

  // Background id_token refresh timer (browser only). Cleared and
  // rescheduled on every successful refresh; never fires in the test
  // harness (init() returns before scheduling).
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;

  async function init(): Promise<void> {
    if (config._testHarness) {
      state.claims = config._testHarness.claims;
      // The default harness token must be a WELL-FORMED JWT with a far-future
      // exp: ensureCredentials() treats an unparseable id_token as expired and
      // (with no refresh_token) tears the whole session down — which would
      // make even the harness's S3-only paths throw "not authenticated".
      const b64url = (o: unknown) =>
        btoa(JSON.stringify(o)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      const fakeJwt = `${b64url({ alg: "none" })}.${b64url({
        ...config._testHarness.claims,
        exp: 4102444800, // 2100-01-01 — never stale in a test run
      })}.x`;
      state.session =
        config._testHarness.session ??
        ({ id_token: fakeJwt, access_token: fakeJwt, token_type: "Bearer" } as OidcSession);
      state.s3 = config._testHarness.s3;
      return;
    }
    const fromCallback = await oidc.handleCallback();
    state.session = fromCallback ?? oidc.getSession();
    if (state.session) {
      state.claims = parseJwt(state.session.id_token);
      scheduleProactiveRefresh();
    }
  }

  function isAuthenticated(): boolean {
    return !!state.session;
  }

  function getClaims(): JwtClaims | null {
    return state.claims;
  }

  function getSession(): OidcSession | null {
    return state.session;
  }

  function credsExpired(): boolean {
    if (!state.creds) return true;
    const expiresAt = new Date(state.creds.expiration).getTime();
    return Number.isNaN(expiresAt) || expiresAt - Date.now() < CREDS_REFRESH_SKEW_MS;
  }

  /** Tear down the session locally and announce that the user must
   *  sign in again. `refreshSession` already clears sessionStorage when a
   *  refresh fails, but the in-memory copy survived — drop it here too so
   *  `signedIn`/`me` flip false and a SessionExpiredError can't be papered
   *  over by a stale token on the next call. */
  function markSessionExpired(reason: SessionExpiredReason): SessionExpiredError {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    state.session = null;
    state.claims = null;
    state.creds = null;
    state.s3 = null;
    events.emit("session.expired", { reason });
    return new SessionExpiredError(reason);
  }

  /** Use the cached refresh_token to mint a fresh id_token and adopt it.
   *  Returns true on success. On failure the session is already gone (the
   *  caller turns that into a SessionExpiredError). */
  async function refreshOidcSession(): Promise<boolean> {
    const refreshed = await oidc.refreshSession();
    if (!refreshed) return false;
    state.session = refreshed;
    state.claims = parseJwt(refreshed.id_token);
    scheduleProactiveRefresh();
    return true;
  }

  /** Exchange the current id_token for STS-style creds via the Identity Pool
   *  (group-aware federation; the pool picks the IAM role from
   *  `cognito:groups`). This is the ONLY credential path — the old direct-STS
   *  fallback could never work (the IAM OIDC provider it needed was retired
   *  platform-wide), so a missing identityPoolId is a config error, not a
   *  mode switch. */
  async function exchangeForCreds(idToken: string): Promise<StsCredentials> {
    if (!cognito.identityPoolId) {
      throw new Error(
        `project "${slug}" config is missing cognito.identityPoolId — the Identity Pool is the only credential path (direct STS was retired). Re-provision the project or add identityPoolId to its s3p.project.json.`,
      );
    }
    return getCredentialsViaIdentityPool({
      region: aws.region,
      identityPoolId: cognito.identityPoolId,
      // Under operator federation the token is a PLATFORM-pool token, so the
      // provider key must name the platform pool to match the federation rule.
      userPoolId: authPool?.poolId ?? cognito.poolId,
      idToken,
    });
  }

  /** Schedule a background refresh ~PROACTIVE_REFRESH_LEAD_MS before the
   *  current id_token's `exp`, so a long-open tab rolls over before AWS
   *  ever sees a dead token. Best-effort: a failed background refresh just
   *  announces session.expired (the modal prompts re-login); it never
   *  throws into the timer. Reschedules itself after each success. */
  function scheduleProactiveRefresh(): void {
    if (typeof setTimeout !== "function" || !state.session) return;
    if (refreshTimer) clearTimeout(refreshTimer);
    const exp = jwtExpiry(state.session.id_token);
    if (exp === null) return; // can't reason about it; on-demand path will refresh
    // Clamp so a token that's already near/past exp fires promptly, and an
    // absurdly long-lived one doesn't overflow setTimeout's 32-bit delay.
    const delay = Math.max(0, Math.min(exp - Date.now() - PROACTIVE_REFRESH_LEAD_MS, 2 ** 30));
    refreshTimer = setTimeout(() => {
      void (async () => {
        if (!(await refreshOidcSession())) markSessionExpired("refresh-failed");
      })();
    }, delay);
  }

  async function ensureCredentials(): Promise<StsCredentials> {
    if (!state.session) throw new Error("not authenticated — call login() first");

    // Proactively refresh the OIDC token when the STS creds are stale OR
    // the cached id_token is itself within skew of its `exp`. Checking the
    // id_token's own expiry means we never hand a dead token to AWS in the
    // common long-idle case (which used to surface as a raw
    // `cognito-identity ... 400 ... Token expired`). Cognito's hosted-UI
    // id_token lifespan is short (default 1h); the refresh_token keeps a
    // long-running tab alive past it.
    const idTokenStale = jwtExpired(state.session.id_token, CREDS_REFRESH_SKEW_MS);
    if (credsExpired() || idTokenStale) {
      if (idTokenStale) {
        const hadRefreshToken = !!state.session.refresh_token;
        if (!(await refreshOidcSession())) {
          // No refresh_token, or the refresh itself was rejected. Don't
          // bother exchanging a token we know is dead — go straight to
          // "sign in again".
          throw markSessionExpired(hadRefreshToken ? "refresh-failed" : "no-refresh-token");
        }
      }
      const t0 = performance.now();
      try {
        state.creds = await exchangeForCreds(state.session.id_token);
      } catch (e) {
        // Retry-once: the token may have died between the staleness check
        // and the call (clock skew, slow request). Force one refresh and
        // retry the exchange before giving up.
        if (!isExpiryError(e)) throw e;
        if (!(await refreshOidcSession())) throw markSessionExpired("refresh-failed");
        try {
          state.creds = await exchangeForCreds(state.session.id_token);
        } catch (e2) {
          // A fresh token was still rejected — nothing left to try.
          throw isExpiryError(e2) ? markSessionExpired("token-rejected") : e2;
        }
      }
      state.s3 = null;
      events.emit("sts.refresh", {
        newExpiration: state.creds.expiration,
        durationMs: Math.round(performance.now() - t0),
      });
    }
    // credsExpired() returns true when state.creds is null, so the branch
    // above always populates it before we reach here.
    return state.creds as StsCredentials;
  }

  async function getCredentials(): Promise<StsCredentials> {
    return ensureCredentials();
  }

  // "first" vs "creds-rotated" — distinguish the initial S3 client build from
  // every subsequent rebuild triggered by ensureCredentials nulling state.s3.
  let s3BuiltOnce = false;

  async function getS3Client(): Promise<S3Client> {
    // Test harness: state.s3 is pre-populated by init(); skip STS entirely.
    if (config._testHarness && state.s3) return state.s3;
    const creds = await ensureCredentials();
    if (!state.s3) {
      const reason: "first" | "creds-rotated" = s3BuiltOnce ? "creds-rotated" : "first";
      state.s3 = new S3Client({
        // No endpoint override — AWS SDK picks the regional S3 endpoint
        // automatically (`s3.{region}.amazonaws.com`). Path style is the
        // safe default for buckets with dots in the name.
        region: aws.region,
        forcePathStyle: true,
        credentials: {
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
          sessionToken: creds.sessionToken,
        },
        // Disable the SDK's clock-skew auto-correction. When MinIO
        // returns a transient 503 / aborted response during STS
        // rotation, the middleware misreads it as a skew, computes a
        // bogus offset, and applies it to every subsequent request —
        // turning a one-off blip into intermittent failures that only
        // a page reload clears. We sign with `new Date()` instead.
        systemClockOffset: 0,
      });
      s3BuiltOnce = true;
      events.emit("s3.client.built", { reason });
    }
    // Defensive: even if the middleware updates systemClockOffset on a
    // bad response, reset it before each call so the next request signs
    // with the true current time. Cheap and idempotent.
    (state.s3 as S3Client & { config: { systemClockOffset: number } }).config.systemClockOffset = 0;
    return state.s3;
  }

  /** Anonymous (visitor) credentials via the Identity Pool's unauthenticated
   *  role. No sign-in required. Throws if the pool has no Identity Pool id or
   *  doesn't allow unauthenticated identities. */
  async function getVisitorCredentials(): Promise<StsCredentials> {
    if (!cognito.identityPoolId) {
      throw new Error("getVisitorCredentials: no identityPoolId configured");
    }
    return getVisitorCredentialsViaIdentityPool({
      region: aws.region,
      identityPoolId: cognito.identityPoolId,
    });
  }

  /** An S3 client scoped to the visitor (unauthenticated) role — for public
   *  reads/writes with no sign-in. Always a fresh client (visitor sessions are
   *  short-lived and identity-less). */
  async function getVisitorS3Client(): Promise<S3Client> {
    const creds = await getVisitorCredentials();
    return new S3Client({
      region: aws.region,
      forcePathStyle: true,
      credentials: {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      },
      systemClockOffset: 0,
    });
  }

  function documents<T = unknown, TCtx = void>(
    docConfig: PlatformDocumentsConfig<TCtx>,
  ): DocumentStore<T, TCtx> {
    const storeBucket = docConfig.bucket ?? bucket;
    const crypto = buildCryptoAdapter(docConfig);
    const inner = createDocuments<T, TCtx>({
      ...docConfig,
      bucket: storeBucket,
      getClient: getS3Client,
      crypto,
    });
    // For event-prefix labels, resolve the prefix lazily via a probe with
    // an empty ctx if possible; otherwise show the function's source label.
    const prefixLabel = (ctx: TCtx) =>
      typeof docConfig.prefix === "function" ? docConfig.prefix(ctx) : docConfig.prefix;

    return instrumentDocuments(inner, prefixLabel, events, docConfig, state);
  }

  const rollups = (() => {
    let bound: ReturnType<typeof createRollups> | null = null;
    function ensureBound() {
      if (!bound) {
        bound = createRollups({
          bucket,
          getClient: getS3Client,
          getAuthor: () => state.claims?.sub,
        });
      }
      return bound;
    }
    return {
      define<TCtx, T>(def: RollupDef<TCtx, T>) {
        return ensureBound().define(def);
      },
    };
  })();

  const analytics = (() => {
    let bound: ReturnType<typeof createAnalytics> | null = null;
    function ensureBound() {
      if (!bound) {
        bound = createAnalytics({ bucket, getClient: getS3Client });
      }
      return bound;
    }
    return {
      define<TCtx>(def: AnalyticsViewDef<TCtx>) {
        return ensureBound().define(def);
      },
    };
  })();

  // Identity module — the IndexedDB open is deferred to first use.
  const identityRaw: IdentityModule = createIdentity({
    bucket,
    getClient: getS3Client,
    storage: config.identityStorage,
  });

  function currentSubOrThrow(op: string): string {
    const sub = state.claims?.sub;
    if (!sub) throw new Error(`platform.identity.${op}: not authenticated`);
    return sub;
  }

  const identity = {
    ensureKeys: () => identityRaw.ensureKeys(currentSubOrThrow("ensureKeys")),
    publishPublicKey: () => identityRaw.publishPublicKey(currentSubOrThrow("publishPublicKey")),
    fetchPublicKey: (sub: string) => identityRaw.fetchPublicKey(sub),
    raw: identityRaw,
  };

  const roles: RolesModule = createRoles({
    bucket,
    getClient: getS3Client,
    identity: identityRaw,
    getSub: () => state.claims?.sub,
  });

  // Group → list-of-subs resolver. Default reads from the JWT for groups
  // the CURRENT user is in (Keycloak puts group membership in the
  // `groups` claim when the realm is configured to do so). For
  // cross-group encryption ("encrypt to a group I'm not in") callers
  // override via setGroupResolver — typically wired through the admin
  // client which can query Keycloak directly.
  let groupResolver: (group: string) => Promise<string[]> = async (group) => {
    const claims = state.claims as { groups?: unknown } | null;
    const myGroups = Array.isArray(claims?.groups) ? (claims?.groups as unknown[]) : [];
    const inGroup = myGroups.some(
      (g) => typeof g === "string" && (g === group || g === `/${group}`),
    );
    if (!inGroup) {
      throw new Error(
        `platform.documents recipients: group "${group}" is not in the current JWT's groups claim — call platform.setGroupResolver(...) to provide a cross-group resolver (typically backed by the admin client).`,
      );
    }
    // Self-knowledge only: we know WE are a member. For richer queries
    // ("who else is in this group?") the caller MUST provide a resolver.
    const sub = state.claims?.sub;
    return sub ? [sub] : [];
  };
  function setGroupResolver(r: (group: string) => Promise<string[]>) {
    groupResolver = r;
  }
  // Test harness can pre-set the group resolver so the documents()
  // recipients pipeline works out of the box.
  if (config._testHarness?.groupResolver) {
    setGroupResolver(config._testHarness.groupResolver);
  }

  /** Translate the friendly `EncryptionSpec` shorthand into the
   *  internal `RecipientSpec` the crypto pipeline already understands.
   *  Centralized so both the new `encryptedFor` field and the legacy
   *  `recipients` field flow through the same code path. */
  function normalizeEncryptionSpec(spec: EncryptionSpec): RecipientSpec {
    if (spec === "author") {
      const sub = state.claims?.sub;
      if (!sub) {
        throw new Error(
          'platform.documents: encryptedFor: "author" requires an authenticated session',
        );
      }
      return { subs: [sub] };
    }
    if (spec === "server") {
      throw new Error(
        'platform.documents: encryptedFor: "server" requires a project-server pubkey publish convention that is not wired in v1 — use `role:` against a server-tier role for now',
      );
    }
    if (typeof spec === "string") {
      if (spec.startsWith("role:")) return { role: spec.slice("role:".length) };
      if (spec.startsWith("group:")) return { group: spec.slice("group:".length) };
      throw new Error(
        `platform.documents: encryptedFor: invalid string "${spec}" — expected "author", "role:X", or "group:Y"`,
      );
    }
    return spec;
  }

  /** Resolve the docConfig's encryption setting into a normalized
   *  `PlatformRecipients<TCtx>` shape, honoring `encryptedFor` first
   *  and falling back to the deprecated `recipients` field. */
  function effectiveRecipients<TCtx>(
    docConfig: PlatformDocumentsConfig<TCtx>,
  ): PlatformRecipients<TCtx> | undefined {
    const e = docConfig.encryptedFor;
    if (e !== undefined) {
      if (typeof e === "function") {
        return async (ctx: TCtx) => normalizeEncryptionSpec(await e(ctx));
      }
      return normalizeEncryptionSpec(e);
    }
    return docConfig.recipients;
  }

  /** Resolve a RecipientSpec into the concrete pubkey list at WRITE time.
   *  Caches per-call so multiple stores using the same group don't re-fetch. */
  async function resolveRecipients(spec: RecipientSpec): Promise<RecipientPublicKey[]> {
    if ("recipients" in spec) return spec.recipients;
    if ("subs" in spec) {
      const out: RecipientPublicKey[] = [];
      for (const sub of spec.subs) {
        const publicKey = await identityRaw.fetchPublicKey(sub);
        out.push({ kind: "user", id: sub, publicKey });
      }
      return out;
    }
    if ("role" in spec) {
      const rolePub = await roles.fetchRolePublicKey(spec.role);
      return [rolePub];
    }
    // group: resolve to CURRENT per-user pubkeys only. We deliberately
    // do NOT auto-include the group's role pubkey here — that would
    // mean anyone who's ever held the role key keeps read access to
    // future posts even after being removed from the group, defeating
    // membership-based revocation. Callers who want the role-key as
    // an additional recipient must combine explicitly via the
    // `recipients` form (or use `{ role: ... }` alone).
    const subs = await groupResolver(spec.group);
    const out: RecipientPublicKey[] = [];
    for (const sub of subs) {
      const publicKey = await identityRaw.fetchPublicKey(sub);
      out.push({ kind: "user", id: sub, publicKey });
    }
    return out;
  }

  /** Build a CryptoAdapter for one documents() call. The adapter:
   *  - on WRAP: resolves recipients via the spec (group/role/subs/explicit)
   *  - on UNWRAP: tries the user's own private key + any held role keys
   *  All async per-call, so membership swaps take effect at the next write
   *  without restarting the SDK. */
  function buildCryptoAdapter<TCtx>(
    docConfig: PlatformDocumentsConfig<TCtx>,
  ): CryptoAdapter | undefined {
    const recipients = effectiveRecipients(docConfig);
    if (!recipients) return undefined;

    return createEnvelopeAdapter({
      resolver: {
        async recipientsFor(_objectKey: string): Promise<RecipientPublicKey[]> {
          // The documents primitive only hands the adapter the objectKey,
          // not the ctx. For static specs we can resolve directly; for
          // function-form `encryptedFor: (ctx) => ...` the caller has to
          // pre-resolve by opening a separate store per ctx-value.
          if (typeof recipients === "function") {
            throw new Error(
              "platform.documents: dynamic `encryptedFor: (ctx) => ...` is not yet supported " +
                "via the auto adapter. For now, use a static spec, OR open one log()/documents() " +
                "per ctx-value.",
            );
          }
          return resolveRecipients(recipients);
        },
        async myKeys(): Promise<RecipientPrivateKey[]> {
          // Pull the user keypair directly from the identity module on
          // every unwrap. This is async (the s3db resolver supports it)
          // and bootstraps the keypair on first use, so callers don't
          // have to remember to call ensureKeys() before each new
          // documents() they open. The identity module caches internally,
          // so the per-call cost is just one Map lookup after the first
          // invocation.
          const out: RecipientPrivateKey[] = [];
          const sub = state.claims?.sub;
          if (sub) {
            try {
              out.push(await identityRaw.getMyPrivateKey(sub));
            } catch {
              // No identity bootstrapped yet for this session — fine,
              // we'll fall back to role keys only.
            }
          }
          out.push(...roles.asPrivateKeys());
          return out;
        },
      },
    });
  }

  // Change B: app.fn enqueues by invoking the per-function Lambda
  // (`s3p-{slug}-fn-{name}`) directly (async Event), which writes the same
  // outputs/{taskId}.json the poll path reads. Falls back to the S3 inbox if the
  // invoke fails (old projects with only the shared runtime).
  const invoker = createInvoker({
    region: aws.region,
    slug,
    getCredentials: async () => {
      const c = await getCredentials();
      return {
        accessKeyId: c.accessKeyId,
        secretAccessKey: c.secretAccessKey,
        sessionToken: c.sessionToken,
      };
    },
    getIdToken: () => state.session?.id_token ?? null,
  });
  const calls = createCalls({
    bucket,
    bucketPrefix,
    paths: paths(),
    getS3Client,
    getClaims: () => state.claims,
    // The transport: invoke the per-function Lambda. Under the test harness
    // there's no Lambda to reach (a real InvokeCommand hits the network and
    // 400s at STS), so a harness may inject a stand-in that plays the runtime.
    // Absent one, enqueueing is a NO-OP: the task is recorded and then nothing
    // runs it, so `call()` polls to its timeout — which is what the tests that
    // assert on the record itself expect.
    enqueue:
      config._testHarness !== undefined
        ? (config._testHarness.enqueue ?? (async () => {}))
        : (name: string, input: unknown, taskId: string) => invoker.enqueue(name, input, taskId),
  });

  // app.functions — registry (name → Function URL) + per-function CloudWatch logs.
  const functionsAdmin = createFunctionsAdmin({
    region: aws.region,
    slug,
    bucket,
    getS3Client,
    getCredentials: async () => {
      const c = await getCredentials();
      return {
        accessKeyId: c.accessKeyId,
        secretAccessKey: c.secretAccessKey,
        sessionToken: c.sessionToken,
      };
    },
  });

  const email = createEmail({
    calls,
    getClient: getS3Client,
    bucket,
    bucketPrefix,
    paths: paths(),
  });

  const secrets = createSecrets({
    getClient: getS3Client,
    bucket,
    secretsPrefix: `${bucketPrefix}secrets/`,
    // Derived by convention — S3 accepts the alias ARN as SSEKMSKeyId.
    // ONE platform-wide key; the runtime role's encryption-context condition
    // scopes decryption per bucket.
    kmsKeyArn: platformSecretsKeyAliasArn(aws.region, accountId),
  });

  // Caller-style credentials provider for SDK admin modules. The STS
  // session the user holds gates which APIs succeed at IAM time.
  const adminCreds = async () => {
    const c = await getCredentials();
    return {
      accessKeyId: c.accessKeyId,
      secretAccessKey: c.secretAccessKey,
      sessionToken: c.sessionToken,
    };
  };

  const users = createUsers({
    region: aws.region,
    userPoolId: cognito.poolId,
    slug,
    credentials: adminCreds,
  });

  const authAdmin = createAuthAdmin({
    region: aws.region,
    userPoolId: cognito.poolId,
    clientId: cognito.clientId,
    bucket: platformBucketName(accountId, slug),
    credentials: adminCreds,
  });

  // app.operators — manage the `/server` operators (platform-pool
  // `{slug}:operator-owner/-admin` groups) via the broker. For a CHILD project
  // the broker is fronted by the PLATFORM pool (consoleAuth = operator
  // federation). For the PLATFORM project itself, the broker is fronted by its
  // OWN pool (it signs in natively; its operators are `console:owner/admin` —
  // the one slug whose operator groups carry no `operator-` prefix). Either way
  // the broker runs in the platform runtime and manages the groups there.
  const operatorBrokerAuth = config.consoleAuth
    ? {
        poolId: config.consoleAuth.poolId,
        identityPoolId: config.consoleAuth.identityPoolId,
        bucket: rootBucketName(accountId),
      }
    : isRootSlug(slug) && cognito.identityPoolId
      ? { poolId: cognito.poolId, identityPoolId: cognito.identityPoolId, bucket }
      : null;
  const operators: OperatorsModule | null = operatorBrokerAuth
    ? createOperators({
        region: aws.region,
        slug,
        consoleAuth: operatorBrokerAuth,
        getIdToken: () => state.session?.id_token ?? null,
        getClaims,
      })
    : null;

  // app.projects — provision/delete projects. The handlers run in the platform
  // runtime, so the module exists only on the root project's own client.
  const projects: ProjectsModule | null = isRootSlug(slug) ? createProjects({ calls }) : null;

  // app.auth — native Cognito auth (no hosted UI). Adopting a session here
  // mirrors what init() does after a redirect callback: persist where the
  // OIDC client reads it, hydrate live state, drop stale creds, and arm the
  // proactive refresh. signOut is local + best-effort token revocation (a
  // native session never created a hosted-UI cookie to clear).
  const auth = createAuth({
    region: aws.region,
    // Same target the OIDC client signs into: the platform/admin pool under
    // federation (`/server`), else this app's own client.
    clientId: authPool?.clientId ?? cognito.clientId,
    onSession: (session) => {
      oidc.setSession(session);
      state.session = session;
      state.claims = parseJwt(session.id_token);
      state.creds = null;
      state.s3 = null;
      scheduleProactiveRefresh();
    },
    onSignOut: () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      oidc.clearSession();
      state.session = null;
      state.claims = null;
      state.creds = null;
      state.s3 = null;
    },
    // Lets `auth.flow().useProvider(name)` reach the hosted-UI redirect — the
    // same one `app.signIn({ identityProvider })` uses.
    onProviderRedirect: (name) => oidc.login({ identityProvider: name }),
  });

  // app.metrics — telemetry (CloudWatch) read directly from the browser with the
  // operator's scoped creds. Gated by the `observe` capability: a member gets
  // AccessDenied, which is the right answer.
  const metrics = createMetrics({
    region: aws.region,
    slug,
    getCredentials: async () => {
      const c = await getCredentials();
      return {
        accessKeyId: c.accessKeyId,
        secretAccessKey: c.secretAccessKey,
        sessionToken: c.sessionToken,
      };
    },
  });

  // app.analytics — product analytics written straight to this project's own
  // bucket. Anonymous visitors use the Identity Pool's UNAUTHENTICATED role,
  // which is granted PutObject on data/_analytics/* and nothing else. No
  // third-party script, no cookie, no data leaving the account.
  const telemetry = createTelemetry({
    bucket,
    getS3Client,
    getVisitorS3Client: () => getVisitorS3Client(),
    isSignedIn: () => !!state.session,
    getSub: () => (state.claims?.sub as string | undefined) ?? null,
  });

  // app.files — blob storage (photos/uploads) under files/, scoped per-user by
  // the `${jwt:sub}` IAM floor. Distinct from s3db (collection/doc/log).
  const files = createFiles({
    bucket,
    getClient: getS3Client,
    getSub: () => (state.claims?.sub as string | undefined) ?? null,
  });

  const transaction = createTransaction({ getClient: getS3Client });

  const roleAdmin: AppRolesModule | null = cognito.identityPoolId
    ? createRolesModule({
        region: aws.region,
        slug,
        bucket,
        userPoolId: cognito.poolId,
        spaClientId: cognito.clientId,
        identityPoolId: cognito.identityPoolId,
        credentials: adminCreds,
        getClient: getS3Client,
        // Browser sessions (enhanced flow) can't call cognito-identity —
        // route the mapping step through the platform broker when the
        // operator transport exists (see sync-role-mappings).
        ...(operators
          ? {
              mappingsBroker: (args: { slug: string; rules: unknown; hasVisitor: boolean }) =>
                operators.invokePlatform("sync-role-mappings", { ...args }),
            }
          : {}),
        // End-users can't be console users — except on the root, whose pool
        // IS the platform pool. Every child is federated (pre-federation
        // projects are a retired shape), so tiers are own-pool on root ONLY.
        ownPoolTiers: isRootSlug(slug),
      })
    : null;

  // The new top-level surface. These are mostly thin façades over the
  // existing modules — the value is in the names and the prepended
  // scope prefix so apps don't have to repeat it.

  /** Resolve the base prefix for a data scope.
   *
   *  THE ONE PLACE the app's data layer decides where anything goes — every
   *  collection and doc is built on this (logs live under their own top-level
   *  `logs/` root, see `logFn`). It reads paths.ts rather than hardcoding a
   *  prefix, so the layout has exactly one definition.
   *
   *  The three scopes are separate prefixes on purpose, because IAM can
   *  only reason about prefixes: `shared` is broadly readable, `private/{sub}` is
   *  reachable only by that user, `admin` only by roles holding `admin-data` /
   *  `read-all`. If they were nested inside one readable prefix, a broad read
   *  grant would defeat all three. */
  function scopedPrefix(scope: DataScope | undefined): string {
    const p = paths();
    if (scope === "private") {
      const sub = state.claims?.sub;
      if (!sub) {
        throw new Error(
          'scope: "private" requires a signed-in session — create private collections/docs after login.',
        );
      }
      return `${bucketPrefix}${p.private(sub)}`;
    }
    if (scope === "admin") return `${bucketPrefix}${p.admin()}`;
    return `${bucketPrefix}${p.shared()}`;
  }

  function collectionFn<T = unknown>(
    name: string,
    cfg: PlatformCollectionConfig<T> & { partition: "author" },
  ): AuthorPartitionedCollection<T>;
  function collectionFn<T = unknown>(
    name: string,
    cfg?: PlatformCollectionConfig<T>,
  ): Collection<T>;
  function collectionFn<T = unknown>(
    name: string,
    cfg: PlatformCollectionConfig<T> = {},
  ): Collection<T> {
    const partitioned = cfg.partition === "author";
    if (partitioned && cfg.scope === "private") {
      throw new Error(
        `collection '${name}': partition: "author" is redundant with scope: "private" — ` +
          "a private collection is already per-user. Drop one of the two.",
      );
    }
    const authorSub = (): string => {
      const sub = state.claims?.sub;
      if (!sub) {
        throw new Error(
          `collection '${name}': partition: "author" needs a signed-in session to write — ` +
            "sign in before put/mine().",
        );
      }
      return sub;
    };
    const coll = createCollection<T>(
      {
        getClient: getS3Client,
        bucket,
        // Thunk, not string: a `private` collection's prefix depends on the
        // signed-in user, and app code legitimately constructs collections at
        // module top level — before login. Resolve when it's actually used.
        basePath: () => `${scopedPrefix(cfg.scope)}${name}/`,
        // Same lazy shape for the author partition (needs the session sub).
        ...(partitioned ? { partitionPrefix: authorSub } : {}),
      },
      cfg,
    );
    if (!partitioned) return coll;
    const pc = coll as PartitionedCollection<T>;
    return Object.assign(pc, {
      mine: () => pc.partition(authorSub()),
      byAuthor: (sub: string) => pc.partition(sub),
    }) satisfies AuthorPartitionedCollection<T>;
  }

  function docFn<T = unknown>(name: string, cfg: PlatformDocConfig = {}): DocHandle<T> {
    return createDoc<T>(
      {
        getClient: getS3Client,
        bucket,
        basePath: () => scopedPrefix(cfg.scope),
        me: () => deriveMe(state.claims, config.slug),
      },
      name,
      cfg,
    );
  }

  function logFn<T = unknown, TCtx = void>(
    name: string | ((ctx: TCtx) => string),
    cfg: PlatformLogConfig<TCtx> = {},
  ): DocumentStore<T, TCtx> {
    // Logs live under the top-level `logs/` root — append-only streams, NOT
    // the database. The member floor reads all of `logs/` and writes only the
    // caller's own author partition (`logs/{stream}/{sub}/…`); a flat stream
    // needs an explicit role grant (`s3p.permissions.json` `grants.logs`).
    const prefix: PlatformDocumentsConfig<TCtx>["prefix"] =
      typeof name === "function"
        ? (((ctx: TCtx) =>
            `${bucketPrefix}${paths().logs(name(ctx))}`) as PlatformDocumentsConfig<TCtx>["prefix"])
        : `${bucketPrefix}${paths().logs(name)}`;
    return documents<T, TCtx>({
      prefix,
      ...(cfg.partition ? { partition: cfg.partition } : {}),
      ...(cfg.encryptedFor ? { encryptedFor: cfg.encryptedFor } : {}),
    } as PlatformDocumentsConfig<TCtx>);
  }

  function viewFn<TCtx>(def: AnalyticsViewDef<TCtx>): AnalyticsView<TCtx> {
    return analytics.define(def);
  }

  // The default DATA PROVIDER: s3db. collectionFn/docFn already encapsulate
  // the S3 key layout + scope resolution, so the provider is a thin, honest
  // wrapper — the value is the SEAM (call sites bind to this interface, not
  // to S3) plus the capability flags apps can branch on.
  // app.permissions / app.can — custom-permission + function-invoke checks
  // against the compiled map (member-readable; written by role sync).
  const permissions = createPermissionsModule({
    getClient: getS3Client,
    bucket,
    groups: () => deriveMe(state.claims, config.slug)?.groups ?? [],
    isAuthenticated,
  });

  const dataProvider: DataProvider = {
    name: "s3",
    capabilities: S3_CAPABILITIES as ProviderCapabilities,
    collection: collectionFn,
    doc: docFn,
  };

  function fnFn(name: string): (input?: unknown, opts?: CallOpts) => Promise<unknown> {
    return (input?: unknown, opts?: CallOpts) => calls.invoke(name, input, opts);
  }

  const domains = createDomains({
    getClient: getS3Client,
    slug,
    consoleBucket: rootBucketName(accountId),
    // Status records live at paths.domainRecord() in THIS project's bucket —
    // platform bookkeeping (`.s3p/domains/`), NOT an app-data collection.
    bucket,
    paths: paths(),
  });

  // app.origins — the bucket's CORS origin list. Direct S3 (PutBucketCORS on
  // this bucket only), not a platform request: unlike a domain attach it needs
  // nothing outside this project. See origins.ts.
  const origins = createOrigins({
    getClient: getS3Client,
    bucket,
    slug,
    region: aws.region,
  });

  // app.dbAdmin — the Database panel's engine: classified tree + raw object
  // CRUD. IAM decides what a given session can actually see or touch.
  const dbAdmin = createDbAdmin({
    bucket,
    bucketPrefix,
    slug,
    getClient: getS3Client,
    sub: () => state.claims?.sub,
  });

  // app.deviceLink — the relay half of `s3p connect` (read the request, seal
  // the answer). Visitor STS by design: approving must work from a tab that
  // only JUST signed in, and the deviceId is the capability.
  const deviceLink = createDeviceLink({
    getVisitorClient: getVisitorS3Client,
    bucket,
    bucketPrefix,
    paths: paths(),
  });

  /** Which log streams exist: one delimiter-list of `logs/` — the folder names
   *  are the stream names. Discovery only; `app.log(name)` reads a stream. */
  async function logStreams(): Promise<string[]> {
    const s3 = await getS3Client();
    const prefix = `${bucketPrefix}${paths().logsRoot()}`;
    const out: string[] = [];
    let token: string | undefined;
    do {
      const res = await s3.send(
        new ListObjectsV2Command({
          Bucket: bucket,
          Prefix: prefix,
          Delimiter: "/",
          ContinuationToken: token,
        }),
      );
      for (const p of res.CommonPrefixes ?? []) {
        const name = p.Prefix?.slice(prefix.length).replace(/\/$/, "");
        if (name) out.push(name);
      }
      token = res.NextContinuationToken;
    } while (token);
    return out.sort();
  }

  return {
    init,
    login: oidc.login,
    logout: oidc.logout,
    isAuthenticated,
    getClaims,
    getSession,
    getCredentials,
    getS3Client,
    getVisitorCredentials,
    getVisitorS3Client,

    // ─── New v1 surface ──────────────────────────────────────────
    get me() {
      return deriveMe(state.claims, config.slug);
    },
    get signedIn() {
      return !!state.session;
    },
    signIn: oidc.login,
    signOut: oidc.logout,
    auth,
    permissions,
    can: permissions.can,
    data: dataProvider,
    metrics,
    telemetry,
    collection: collectionFn,
    log: logFn,
    logStreams,
    doc: docFn,
    view: viewFn,
    fn: fnFn,
    /** One module for everything function-shaped: registry + logs
     *  (functionsAdmin) and the invocation ledger (calls). */
    functions: { ...functionsAdmin, ...calls },
    secrets,
    email,
    domains,
    origins,
    deviceLink,
    files,
    users,
    authAdmin,
    /** Operator management (the platform identities allowed into this
     *  project's `/server`). Non-null only under operator federation. */
    operators,
    projects,
    /** Role taxonomy management. Available only when the project is
     *  configured with an Identity Pool. */
    roleAdmin,
    dbAdmin,
    /** Best-effort sequential write group. NOT atomic — returns a
     *  rollback() compensator on failure. */
    transaction,

    // ─── Legacy surface ──────────────────────────────────────────
    documents,
    rollups,
    identity,
    roles,
    setGroupResolver,
    analytics,
    events,
  };
}

// ─────────────────────────────────────────────────────────────────────
// connect() — auto-bootstrap helper attached to createPlatformClient
// ─────────────────────────────────────────────────────────────────────

/** Options for the auto-bootstrap helper. */
export interface ConnectOptions {
  /** Honor the `?autologin=1` query param by redirecting straight to
   *  Keycloak's login when the user isn't already signed in. Matches
   *  the convention `s3p dev --auto-login` already uses. */
  autologin?: boolean;
  /** Override the OIDC redirect URI (used by /server-mode pages so the
   *  callback doesn't land on the /spa client's redirect). */
  redirectUri?: string;
  /** Override the post-logout redirect URI. */
  postLogoutRedirectUri?: string;
  /** Override the browser-wide localStorage used for OIDC tokens. */
  tokenStorage?: Storage;
}

/** Shape we expect from `/__s3p/project.js` (synthesized by `s3p dev`
 *  and `s3p build`). Loose because the SDK doesn't depend on the CLI's
 *  ProjectConfig type — config arrives as data. */
interface AutoConfigShape {
  slug: string;
  aws: { region: string; accountId: string };
  cognito: {
    poolId: string;
    clientId: string;
    identityPoolId: string;
    domain?: string;
  };
}

async function loadAutoConfig(): Promise<AutoConfigShape> {
  // Dynamic-imported so the SDK doesn't synchronously require this file
  // to exist (apps that pass config explicitly never trigger this path).
  // The path is stashed in a const so TypeScript doesn't try to statically
  // resolve `/__s3p/project.js` as a module — at runtime the dev server
  // (or build output) serves the synthesized module from this URL.
  const url = "/__s3p/project.js";
  const mod = (await import(/* @vite-ignore */ url)) as {
    s3pProject?: AutoConfigShape;
    default?: AutoConfigShape;
  };
  const cfg = mod.s3pProject ?? mod.default;
  if (!cfg) {
    throw new Error("createPlatformClient.connect(): /__s3p/project.js did not export `s3pProject`");
  }
  return cfg;
}

/** Top-level helper that condenses the boot dance (init → auth check →
 *  STS warm-up) into one await. Two calling forms:
 *
 *    await createPlatformClient.connect()                     // auto-load /__s3p/project.js
 *    await createPlatformClient.connect(s3pProject)            // explicit config
 *    await createPlatformClient.connect({ autologin: true })  // auto-load + autologin
 *    await createPlatformClient.connect(s3pProject, { autologin: true })
 *
 *  Returns a `PlatformClient` ready to use — `app.signedIn`, `app.me`,
 *  and `app.collection(...)` etc. all work immediately when signed in.
 *  When NOT signed in, the helper does NOT redirect unless `autologin`
 *  is set AND the URL carries `?autologin=1` — apps decide UX. */
export async function connect(
  configOrOpts?: PlatformConfig | AutoConfigShape | ConnectOptions,
  maybeOpts?: ConnectOptions,
): Promise<PlatformClient> {
  let cfg: PlatformConfig;
  let opts: ConnectOptions;

  if (configOrOpts && "cognito" in configOrOpts) {
    // explicit-config form
    const provided = configOrOpts as PlatformConfig | AutoConfigShape;
    cfg = toPlatformConfig(provided);
    opts = maybeOpts ?? {};
  } else {
    // auto-load form
    const auto = await loadAutoConfig();
    cfg = toPlatformConfig(auto);
    opts = (configOrOpts as ConnectOptions | undefined) ?? {};
  }

  if (opts.redirectUri) cfg = { ...cfg, redirectUri: opts.redirectUri };
  if (opts.postLogoutRedirectUri) {
    cfg = { ...cfg, postLogoutRedirectUri: opts.postLogoutRedirectUri };
  }
  if (opts.tokenStorage) cfg = { ...cfg, tokenStorage: opts.tokenStorage };

  const client = createPlatformClient(cfg);
  await client.init();

  if (!client.isAuthenticated() && opts.autologin) {
    const url = new URL(location.href);
    if (url.searchParams.has("autologin")) {
      url.searchParams.delete("autologin");
      history.replaceState({}, "", url.toString());
      // login() redirects away; the post-callback reload re-enters this path.
      client.login();
      return client;
    }
  }

  if (client.isAuthenticated()) {
    // Warm STS so getS3Client() is cheap on the first data call. Tolerate
    // failure here — apps may want to render a banner explaining it.
    try {
      await client.getS3Client();
    } catch {
      // Surfaced via app.signedIn === true but app.getS3Client() throws.
    }
  }

  return client;
}

/** Map the project's published config into the client's config.
 *
 *  THIS IS AN ALLOWLIST, and that is the point — the config a browser fetches is
 *  public, so we copy only what we understand. But it also means EVERY new field
 *  must be added here or it is silently dropped: the retired `layout` field was
 *  lost exactly this way once, and nothing errored — the data just went to the
 *  wrong place. If you add a config field, add it here. */
function toPlatformConfig(input: PlatformConfig | AutoConfigShape): PlatformConfig {
  return {
    slug: input.slug,
    aws: { ...input.aws },
    cognito: { ...input.cognito },
    // consoleAuth is DELIBERATELY NOT CARRIED HERE.
    //
    // Its presence switches the client into OPERATOR FEDERATION — sign in against
    // the PLATFORM pool rather than this project's own. That is right for the
    // console (which passes it explicitly when it mounts a project's admin view)
    // and catastrophically wrong for the app itself: a project's end-users live in
    // the PROJECT's pool, so carrying it here made every customer's login fail with
    // "Incorrect username or password" against a pool they were never in.
    //
    // I added it here speculatively while adding `layout`, and it broke the test
    // app's sign-in immediately. The field was omitted on purpose. Leave it out.
  };
}

// Attach connect() to createPlatformClient so apps can write
// `createPlatformClient.connect(...)`. Doing it after the export so the
// function object has the symbol attached without polluting the type.
(
  createPlatformClient as typeof createPlatformClient & {
    connect: typeof connect;
  }
).connect = connect;

export type { Me } from "./me.ts";
export type { Collection, CollectionConfig } from "./collection.ts";
export type { DocConfig, DocHandle } from "./doc.ts";

/** Wrap a DocumentStore so every method emits a platform event with timing.
 *  Also handles the author-partition auto-injection (sub from claims).
 *  Kept as a free function so createPlatformClient stays readable. */
function instrumentDocuments<T, TCtx>(
  inner: DocumentStore<T, TCtx>,
  prefixLabel: (ctx: TCtx) => string,
  events: PlatformEvents,
  docConfig: PlatformDocumentsConfig<TCtx>,
  state: ClientState,
): DocumentStore<T, TCtx> {
  function errMsg(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
  }

  return {
    async append(ctx: TCtx, body: T, opts: AppendOpts = {}) {
      const t0 = performance.now();
      const prefix = prefixLabel(ctx);
      let injected = opts;
      if (docConfig.partition === "author") {
        const sub = state.claims?.sub;
        if (!opts.authorSub && !sub) {
          throw new Error(
            "platform.documents(...).append: not authenticated and no authorSub supplied",
          );
        }
        injected = { ...opts, authorSub: opts.authorSub ?? sub };
      }
      try {
        const entry = await inner.append(ctx, body, injected);
        events.emit("documents.append", {
          prefix,
          key: entry.key,
          durationMs: Math.round(performance.now() - t0),
          ok: true,
        });
        return entry;
      } catch (e) {
        events.emit("documents.append", {
          prefix,
          durationMs: Math.round(performance.now() - t0),
          ok: false,
          error: errMsg(e),
        });
        throw e;
      }
    },

    list(ctx: TCtx, opts?: ListOpts) {
      // Wrap the async-iterable so we can emit once at end-of-iteration.
      const prefix = prefixLabel(ctx);
      const inner$ = inner.list(ctx, opts);
      const t0 = performance.now();
      let count = 0;
      return {
        async *[Symbol.asyncIterator]() {
          try {
            for await (const entry of inner$) {
              count++;
              yield entry;
            }
            events.emit("documents.list", {
              prefix,
              count,
              durationMs: Math.round(performance.now() - t0),
              ok: true,
            });
          } catch (e) {
            events.emit("documents.list", {
              prefix,
              count,
              durationMs: Math.round(performance.now() - t0),
              ok: false,
              error: errMsg(e),
            });
            throw e;
          }
        },
      };
    },

    async get(ctx: TCtx, key: string) {
      const t0 = performance.now();
      const prefix = prefixLabel(ctx);
      try {
        const entry = await inner.get(ctx, key);
        events.emit("documents.get", {
          prefix,
          key,
          durationMs: Math.round(performance.now() - t0),
          ok: true,
        });
        return entry;
      } catch (e) {
        events.emit("documents.get", {
          prefix,
          key,
          durationMs: Math.round(performance.now() - t0),
          ok: false,
          error: errMsg(e),
        });
        throw e;
      }
    },

    async remove(ctx: TCtx, key: string) {
      const t0 = performance.now();
      const prefix = prefixLabel(ctx);
      try {
        await inner.remove(ctx, key);
        events.emit("documents.remove", {
          prefix,
          key,
          durationMs: Math.round(performance.now() - t0),
          ok: true,
        });
      } catch (e) {
        events.emit("documents.remove", {
          prefix,
          key,
          durationMs: Math.round(performance.now() - t0),
          ok: false,
          error: errMsg(e),
        });
        throw e;
      }
    },
  };
}
